export const LEGAL_DOCUMENT_VERSIONS = {
  privacy: "2026-10-06",
  terms: "2026-10-06",
  activitySafety: "2026-10-06",
};

export const LEGAL_DOCUMENTS = {
  privacy: {
    title: "Privacy Policy",
    version: LEGAL_DOCUMENT_VERSIONS.privacy,
    lastUpdated: "6 October 2026",
    summary:
      "How Plasticise collects, uses, stores, shares and protects personal and health information.",
    introduction: [
      "Plasticise Pty Ltd (Plasticise, we, us or our) respects your privacy and is committed to handling personal information responsibly. This Privacy Policy explains how we collect, use, disclose, store and protect personal information when you use Plasticise or interact with us.",
      "Plasticise is a rehabilitation and home-program platform used by therapists and clients. We aim to handle personal information in accordance with the Privacy Act 1988 (Cth), including the Australian Privacy Principles where they apply.",
    ],
    sections: [
      {
        heading: "1. Information we may collect",
        paragraphs: [
          "The information we collect depends on how you use Plasticise and whether you are a therapist, client, clinic representative or other user.",
        ],
        bullets: [
          "Account and contact information, such as your name, email address, mobile number and login details.",
          "Professional information for therapists, such as discipline, clinic and account or subscription information.",
          "Health and rehabilitation information for clients where it is entered into Plasticise, including programs, activities, instructions, media, completion information and feedback.",
          "Communications with Plasticise, including support requests and feedback.",
          "Technical and usage information needed to operate, secure and improve the service, such as device, browser, IP address, login and application usage information.",
          "Payment or billing information where relevant. Payment details may be processed by a payment provider rather than stored directly by Plasticise.",
        ],
      },
      {
        heading: "2. Health and other sensitive information",
        paragraphs: [
          "Health information is sensitive information. Where required, Plasticise relies on consent or another lawful basis to collect and handle health information for the purpose of providing the service. Where a client cannot provide their own agreement, an appropriately authorised parent, guardian or representative may provide agreement on the client's behalf.",
          "For clients, information may be entered by you, your therapist, your clinic or another authorised person involved in your care. You should speak with your therapist or clinic if you have questions about information they have entered into Plasticise about you.",
        ],
      },
      {
        heading: "3. How we collect information",
        paragraphs: [
          "We may collect information directly from you when you create or use an account, complete activities, upload information, contact us or change your settings. We may also receive information from an authorised therapist, clinic, support person or service provider where this is necessary to provide Plasticise.",
        ],
      },
      {
        heading: "4. How we use information",
        bullets: [
          "Provide, maintain and secure Plasticise.",
          "Create and manage therapist and client accounts.",
          "Allow therapists to prescribe, share, review and adapt programs and activities.",
          "Allow clients to access programs, complete activities and provide feedback.",
          "Send service messages, reminders and notifications where enabled.",
          "Provide support and respond to enquiries.",
          "Understand product usage and improve Plasticise.",
          "Manage subscriptions, billing and administration.",
          "Meet legal, regulatory, safety and security obligations.",
        ],
      },
      {
        heading: "5. When information may be shared",
        paragraphs: [
          "We do not sell personal information. We may disclose information where reasonably necessary to provide Plasticise, including to authorised therapists or clinics involved in a client's care and to service providers that support hosting, communications, analytics, security, payment processing or other application functions.",
          "We may also disclose information where required or authorised by law, to respond to legal process, or where reasonably necessary to protect the rights, safety or security of Plasticise, our users or others.",
          "Some service providers may process or store information outside Australia. The locations and providers used by Plasticise may change over time. Where Australian privacy law requires it, we take reasonable steps in relation to overseas handling of personal information.",
        ],
      },
      {
        heading: "6. Security",
        paragraphs: [
          "Plasticise uses technical and organisational measures designed to protect personal information from misuse, interference, loss and unauthorised access, modification or disclosure. No internet-based service can guarantee absolute security.",
          "Users are responsible for keeping their login details secure and should contact us promptly if they believe their account has been accessed without permission.",
        ],
      },
      {
        heading: "7. Retention and deletion",
        paragraphs: [
          "We retain information for as long as reasonably necessary to provide Plasticise, meet legal and regulatory requirements, resolve disputes, maintain appropriate records and protect the security and integrity of the service.",
          "Deletion or account closure requests may be subject to legal, clinical, contractual or record-retention requirements. Where information no longer needs to be retained, we will take reasonable steps to delete or de-identify it.",
        ],
      },
      {
        heading: "8. Access and correction",
        paragraphs: [
          "You may ask to access or correct personal information Plasticise holds about you, subject to applicable law. In some cases, client information is controlled or maintained by the therapist or clinic that provides care, and we may direct the request to them where appropriate.",
        ],
      },
      {
        heading: "9. Notifications, cookies and similar technologies",
        paragraphs: [
          "Plasticise may use browser storage, cookies or similar technologies where needed for authentication, preferences, security, performance and application functionality. You can control some browser permissions and notification settings through your device or Plasticise settings.",
        ],
      },
      {
        heading: "10. Complaints and questions",
        paragraphs: [
          "If you have a privacy question, request or complaint, contact admin@plasticise.app. We will review the matter and respond within a reasonable period. You may also have the right to contact the Office of the Australian Information Commissioner.",
        ],
      },
      {
        heading: "11. Changes to this policy",
        paragraphs: [
          "We may update this Privacy Policy as Plasticise, our service providers or legal requirements change. We will update the date and version of the policy and, where appropriate, notify users of material changes.",
        ],
      },
      {
        heading: "12. Contact",
        paragraphs: [
          "Plasticise Pty Ltd — Sydney, Australia. Email: admin@plasticise.app. Website: plasticise.app.",
        ],
      },
    ],
  },

  terms: {
    title: "Terms of Use",
    version: LEGAL_DOCUMENT_VERSIONS.terms,
    lastUpdated: "6 October 2026",
    summary:
      "The rules for using Plasticise, including accounts, professional responsibilities, subscriptions and acceptable use.",
    introduction: [
      "These Terms of Use apply to access to and use of Plasticise, a rehabilitation and home-program platform operated by Plasticise Pty Ltd (ACN 679805764). By agreeing to these Terms or continuing to use Plasticise after acceptance, you agree to be bound by them.",
      "If you do not agree to these Terms, you must not use Plasticise.",
    ],
    sections: [
      {
        heading: "1. What Plasticise provides",
        paragraphs: [
          "Plasticise provides digital tools that allow therapists to create, prescribe, share and review rehabilitation or home programs and allows clients to access and engage with activities prescribed for them.",
          "Plasticise is a technology platform. It does not replace professional clinical judgement, diagnosis, treatment, emergency care or the relationship between a client and their treating health professional.",
        ],
      },
      {
        heading: "2. Accounts and eligibility",
        bullets: [
          "You must provide accurate and current information when creating or using an account.",
          "You must keep your login details secure and must not allow another person to use your account unless Plasticise expressly supports that access.",
          "You must notify Plasticise if you become aware of unauthorised access to your account.",
          "You must be legally able to agree to these Terms. Where a client cannot provide their own agreement, an appropriately authorised parent, guardian or representative may agree on the client's behalf. A representative must confirm that they have authority to do so.",
        ],
      },
      {
        heading: "3. Therapist responsibilities",
        paragraphs: [
          "Therapists remain responsible for their professional judgement, clinical decisions, scope of practice, client consent processes and the suitability of any program or activity they prescribe through Plasticise.",
          "Therapists must only access, upload or share client information where they are authorised to do so and must use Plasticise consistently with their professional, privacy, record-keeping and organisational obligations.",
        ],
      },
      {
        heading: "4. Client responsibilities",
        paragraphs: [
          "Clients should follow the instructions provided by their treating therapist and use Plasticise only for the program made available to them. Clients should contact their therapist if an activity is unclear, unsuitable or feels unsafe.",
          "Client safety information is set out separately in the Activity Safety Acknowledgement.",
        ],
      },
      {
        heading: "5. Subscriptions and payment",
        paragraphs: [
          "Access for therapists or clinics may depend on a trial, paid subscription, sponsored access or another arrangement agreed with Plasticise. Client access is generally provided through the therapist or clinic that uses Plasticise.",
          "Subscription fees, billing periods, trial arrangements and payment terms may be set out in an order, agreement, invoice or other subscription information provided by Plasticise. Unless otherwise agreed, applicable fees must be paid when due.",
          "Nothing in these Terms excludes rights or remedies that cannot lawfully be excluded, including rights under the Australian Consumer Law.",
        ],
      },
      {
        heading: "6. Acceptable use",
        bullets: [
          "Do not use Plasticise unlawfully, fraudulently or in a way that infringes another person's rights.",
          "Do not attempt to gain unauthorised access to accounts, systems or data.",
          "Do not upload malicious code or deliberately interfere with the operation or security of Plasticise.",
          "Do not copy, reverse engineer, resell or commercially exploit Plasticise except where expressly permitted by law or by written agreement.",
          "Do not upload content you are not authorised to use or share.",
        ],
      },
      {
        heading: "7. Content and media",
        paragraphs: [
          "You retain rights you hold in content you upload to Plasticise. You grant Plasticise the limited rights reasonably necessary to host, process, display, transmit, back up and otherwise handle that content for the purpose of providing and supporting the service.",
          "Therapists and clinics are responsible for ensuring they have appropriate authority to upload client-related content, including images, videos and health information.",
        ],
      },
      {
        heading: "8. Privacy",
        paragraphs: [
          "Personal information is handled as described in the Plasticise Privacy Policy. Users must also comply with their own privacy and confidentiality obligations when entering, accessing or sharing information through Plasticise.",
        ],
      },
      {
        heading: "9. Availability and changes",
        paragraphs: [
          "We aim to keep Plasticise reliable and available, but access may occasionally be interrupted by maintenance, updates, third-party services, network issues, security events or circumstances outside our reasonable control.",
          "We may update, improve, replace or discontinue features where reasonably necessary. We will use reasonable efforts to communicate material changes that significantly affect use of the service.",
        ],
      },
      {
        heading: "10. Intellectual property",
        paragraphs: [
          "Plasticise and its underlying software, branding, design, content and functionality are owned by or licensed to Plasticise Pty Ltd and are protected by intellectual property laws. Except for the limited right to use the service under these Terms, no ownership rights are transferred to you.",
        ],
      },
      {
        heading: "11. Disclaimers and liability",
        paragraphs: [
          "To the extent permitted by law, Plasticise is provided on an as-available basis and we do not guarantee that it will be uninterrupted or error-free. Therapists remain responsible for clinical care and clients remain responsible for following their therapist's instructions and seeking appropriate help when needed.",
          "Nothing in these Terms excludes, restricts or modifies any consumer guarantee, right or remedy that cannot lawfully be excluded. To the extent permitted by law, Plasticise is not liable for indirect or consequential loss arising from use of the service.",
        ],
      },
      {
        heading: "12. Suspension and termination",
        paragraphs: [
          "We may suspend or terminate access where reasonably necessary because of non-payment, security concerns, unlawful use, serious or repeated breach of these Terms, or where we are required to do so by law. Subscription termination may also be governed by a separate clinic or subscription agreement.",
          "You may stop using Plasticise at any time, subject to any separate subscription or contractual commitments.",
        ],
      },
      {
        heading: "13. Changes to these Terms",
        paragraphs: [
          "We may update these Terms from time to time. We will update the version and date and, where appropriate, notify users of material changes. If renewed acceptance is required, Plasticise may ask you to agree to the updated Terms before continuing.",
        ],
      },
      {
        heading: "14. Governing law",
        paragraphs: [
          "These Terms are governed by the laws of New South Wales, Australia. Subject to any rights that cannot be excluded, disputes relating to these Terms are subject to the courts and tribunals with jurisdiction in New South Wales.",
        ],
      },
      {
        heading: "15. Contact",
        paragraphs: [
          "Questions about these Terms can be sent to admin@plasticise.app.",
        ],
      },
    ],
  },

  activitySafety: {
    title: "Activity Safety Acknowledgement",
    version: LEGAL_DOCUMENT_VERSIONS.activitySafety,
    lastUpdated: "6 October 2026",
    summary:
      "Important safety information for clients completing activities prescribed through Plasticise.",
    introduction: [
      "Plasticise helps you access and complete activities prescribed by your therapist between appointments. Your program has been selected or created for you by your treating therapist, but your therapist may not be supervising you while you complete activities.",
      "Activities can include physical, communication, cognitive, functional or other rehabilitation tasks. The risks will depend on the activity, your health and your environment.",
    ],
    sections: [
      {
        heading: "1. Follow your therapist's instructions",
        paragraphs: [
          "Complete activities only as instructed by your therapist. If an instruction is unclear, your health changes, or you are unsure whether an activity remains appropriate for you, contact your therapist before continuing.",
        ],
      },
      {
        heading: "2. Possible risks",
        paragraphs: [
          "Depending on the activity, risks may include pain or discomfort, fatigue, dizziness, loss of balance or falls, aggravation of an existing condition or injury, a new injury, changes in blood pressure or other symptoms, or other complications relevant to your individual health and program.",
          "Some activities may involve equipment, food, communication tasks, mobility, household tasks or other environments that have their own risks. Your therapist can advise you about risks specific to your program.",
        ],
      },
      {
        heading: "3. Stop if something feels unsafe",
        paragraphs: [
          "Stop an activity if you experience unexpected or significant pain, dizziness, shortness of breath, fainting, new neurological symptoms, a marked worsening of symptoms, or if the activity otherwise feels unsafe.",
          "Contact your therapist for advice about whether and how to continue. If you believe you are experiencing a medical emergency, seek urgent medical assistance using the emergency services available in your location.",
        ],
      },
      {
        heading: "4. Make your environment as safe as possible",
        bullets: [
          "Use equipment only as instructed.",
          "Choose an appropriate space and remove avoidable hazards where possible.",
          "Use supervision or assistance if your therapist has recommended it.",
          "Do not continue an activity when you cannot safely follow the prescribed instructions.",
        ],
      },
      {
        heading: "5. Plasticise is not real-time supervision",
        paragraphs: [
          "Plasticise does not continuously monitor you while you complete activities and cannot assess your condition in real time. Feedback or completion information entered into Plasticise does not replace contacting a health professional when you need advice or care.",
        ],
      },
      {
        heading: "6. Your acknowledgement",
        paragraphs: [
          "By accepting this Activity Safety Acknowledgement, you confirm that you understand activities may involve risks, that the client should follow the instructions provided by their therapist, stop and seek advice if an activity feels unsafe, and that Plasticise is not a substitute for real-time clinical supervision or emergency care. If you are accepting on behalf of the client, you also confirm that you have authority to provide this acknowledgement for them.",
          "This acknowledgement is governed by the laws of New South Wales, Australia, subject to any rights that cannot lawfully be excluded.",
        ],
      },
    ],
  },
};
